import { Component, Input } from '@angular/core';
import { PopoverController } from '@ionic/angular';

import { ConfTypes, ParticipantFsDaily, ParticipantFsDolby } from '@sc/types';
import { DolbyService } from '../../services/dolby/dolby.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { ChatService } from '../../services/chat/chat.service';
import { UserService } from '../../services/user/user.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { RoleLimits } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { RenameParticipantMenuComponent } from '../rename-participant-menu/rename-participant-menu.component';
import { ToastrService } from 'ngx-toastr';
import { WalletService } from '../../services/wallet/wallet.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-participant-menu',
  templateUrl: './participant-menu.component.html',
  styleUrls: ['./participant-menu.component.scss'],
})
export class ParticipantMenuComponent {
  @Input() participant: ParticipantFsDolby | ParticipantFsDaily;
  @Input() appearance: 'the stage' | 'backstage';
  activeUser$ = this.userService.activeUser$;
  studioSession$ = this.sessionsService.studioSession$;
  participantId: string;
  role$ = this.rolesService.role$;
  roleLimits = RoleLimits;

  constructor(
    private popoverController: PopoverController,
    private userService: UserService,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private sessionsService: SessionsService,
    private chatService: ChatService,
    private analyticsService: AnalyticsService,
    private studioSidebarService: StudioSidebarService,
    private toastrService: ToastrService,
    private walletService: WalletService,
    private rolesService: RolesService
  ) {}

  /**
   * Renames the participant
   */
  async rename(event: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: RenameParticipantMenuComponent,
      componentProps: { participant: this.participant },
      event,
    });
    await menu.present();
  }

  /**
   * Starts direct message chat group with participant
   */
  async directMessage() {
    await this.popoverController.dismiss();
    await this.chatService.directMessage(this.participant);
    await this.analyticsService.track('sent direct message', { targetParticipantUID: this.participant.uid });
  }

  /**
   * Shows the participant stats in the side panel
   */
  showStats() {
    this.studioSidebarService.toggleStats(this.participantId);
    this.popoverController.dismiss();
  }

  /**
   * Prompts the participant to move between
   * the stage and backstage
   */
  async moveTo(destination: 'stage' | 'backstage') {
    const availability = await this.dolbyService.checkSeatCountAvailability();
    if (destination === 'stage' ? !availability.stageSeatAvailable : !availability.backstageSeatAvailable) {
      this.toastrService.info(
        `${
          destination === 'stage'
            ? this.walletService.studioPlan$.value.inStudio
            : this.walletService.studioPlan$.value.backstage
        } is the maximum for your ${this.walletService.studioPlan$.value.name} plan.` +
          `${
            this.walletService.studioPlan$.value.name.includes('studio')
              ? ''
              : ' If you need more seats please consider upgrading your plan!'
          }`,
        `${
          destination === 'stage' ? availability.stageParticipantCount : availability.backstageParticipantCount
        } Participants are already on The ${destination === 'stage' ? 'Stage' : 'Backstage'}`,
        {
          progressBar: false,
          closeButton: true,
          tapToDismiss: false,
          disableTimeOut: true,
          toastComponent: GeneralToastComponent,
        }
      );
      this.popoverController.dismiss();
      return;
    }

    this.popoverController.dismiss();
    if (this.participantId === this.activeUser$.value.uid && destination === 'stage') {
      this.dolbyService.leaveConversation(false);
      window.location.href = `/studio/${this.studioSession$.value.showID}/session/${this.studioSession$.value.sessionID}`;
    } else {
      await this.dolbyService.promptParticipantToMove(this.participant, destination);
    }
  }

  /**
   * Removes the participant from the session
   */
  async removeFromSession() {
    await this.popoverController.dismiss();
    if (this.studioSession$.value?.confType === ConfTypes.DAILY) {
      this.dailyService.kickFromConversation(this.participant);
    } else {
      await this.dolbyService.kickFromConversation(this.participant);
    }
    await this.analyticsService.track('removed participant from session', {
      targetParticipantUID: this.participant.uid,
    });
  }
}
